import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import trigramCompare from 'trigram-similarity';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraUserLiteData } from '../../../../api/endpoints/jira';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../../concepts/objects';
import { Input, Menu } from '../../../../ui';
import { FixedScroller } from '../../../../ui/Scrollers';
import { useResolve } from '../../../Container';
import { UIField } from '../../../canvas/entities/UIField';
import { PopperFieldEditor, PopperFieldEditorContentProps } from '../../../canvas/workspace/adapters/PopperFieldEditor';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { jiraCardAssign } from '../AtlassianCommands';
import { JiraCard } from '../JiraCard';
import { JiraOAuthRequiredPopperContent } from './JiraOAuthRequiredPopperContent';
import { JiraPopperActionMenu } from './JiraPopperActionMenu';

const useJiraCardAssigneeQuery = (cards: JiraCard[]) => {
    const api = useResolve(ApiClient);

    // If we multiple cards, use the project
    if (cards.length > 1) {
        const jiraProjectId = cards[0].project;
        return useQuery({
            queryKey: ['jira.issues.assignees.list', 'project', jiraProjectId],
            queryFn: async () => api.exec(v2.jira.issues.assignees.list('project', jiraProjectId)),
        });
    }
    // Otherwise just use the id of the issue
    else {
        const jiraIssueKey = cards[0].key; // Note this is key not id (shrug)
        return useQuery({
            queryKey: ['jira.issues.assignees.list', 'issueKey', jiraIssueKey],
            queryFn: async () => api.exec(v2.jira.issues.assignees.list('issueKey', jiraIssueKey)),
        });
    }
};

export const JiraAssigneeMenu = ({ cards, onClose }: { cards: JiraCard[], onClose(): void }) => {

    const objects = useResolve(ObjectManager);
    const dispatch = useResolve(CommandDispatcher);
    const [search, setSearch] = useState('');

    const { isLoading, data } = useJiraCardAssigneeQuery(cards);

    const filteredData = useMemo(() => {
        if (!data) return null;
        if (!search?.trim()) return data;
        const lookup = new Map(data.map(x => [x, trigramCompare(x.displayName, search)]));
        const avg = Array.from(lookup.values()).reduce((a, b) => a + b, 0) / lookup.size;
        const first = data.filter(x => trigramCompare(x.displayName, search) > .25);
        if (first.length) return first;
        return data.filter(x => trigramCompare(x.displayName, search) > avg);
    }, [search, data]);

    const onClick = (tm: JiraUserLiteData) => {
        objects.update(() => {
            dispatch(jiraCardAssign, [tm, cards.map(x => x.id)]);
        });
        onClose();
    };

    return <JiraPopperActionMenu loading={isLoading} label="Assign To">
        {data?.length > 10 &&
            <Flexer padding="0 .5rem .5rem .5rem">
                <Input type="text" placeholder="Search" value={search} onChange={setSearch} autoFocus compact />
            </Flexer>
        }
        <FixedScroller maxHeight="12">
            {filteredData?.map((tm, i) =>
                <Menu.Item role="menuitem" aria-label={tm.displayName} key={i} onClick={() => onClick(tm)}>
                    <img style={{ width: '1.5rem' }} src={tm.avatarUrl} />
                    <Menu.ItemText>{tm.displayName}</Menu.ItemText>
                </Menu.Item>
            )}
        </FixedScroller>
    </JiraPopperActionMenu>;
};

const JiraAssigneeMenuFieldEditor = ({ field, onClose }: PopperFieldEditorContentProps) =>
    <JiraAssigneeMenu cards={[field.owner.cast(JiraCard)]} onClose={onClose} />;

export const JiraAssigneeField = (owner: JiraCard): UIField => ({
    ui: PopperFieldEditor(JiraOAuthRequiredPopperContent(JiraAssigneeMenuFieldEditor)),
    owner,
    name: 'assignee',
    bounds: owner.layout.assignee,
});
